import { useEffect, useRef, useCallback, type RefObject } from 'react';
import { isNearBottom, scrollContainerToBottom } from '../utils/chatScroll';

export function useChatAutoScroll({
  containerRef,
  conversationKey,
  messageCount = 0,
  enabled = true,
}: {
  containerRef: RefObject<HTMLElement | null>;
  conversationKey: string | null | undefined;
  messageCount?: number;
  enabled?: boolean;
}) {
  const stickToBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const lastConversationKeyRef = useRef(conversationKey);

  useEffect(() => {
    if (lastConversationKeyRef.current !== conversationKey) {
      lastConversationKeyRef.current = conversationKey;
      stickToBottomRef.current = true;
      prevMessageCountRef.current = 0;
    }
  }, [conversationKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;

    const onScroll = () => {
      stickToBottomRef.current = isNearBottom(el);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [containerRef, conversationKey, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    const prev = prevMessageCountRef.current;
    const count = messageCount;
    const isInitialLoad = prev === 0 && count > 0;
    const hasNewMessages = count > prev;

    if (isInitialLoad || (hasNewMessages && stickToBottomRef.current)) {
      requestAnimationFrame(() => {
        scrollContainerToBottom(
          el,
          hasNewMessages && !isInitialLoad ? 'smooth' : 'auto'
        );
      });
    }

    prevMessageCountRef.current = count;
  }, [messageCount, conversationKey, containerRef, enabled]);

  const scrollToBottom = useCallback(
    (behavior: 'auto' | 'smooth' = 'smooth') => {
      const el = containerRef.current;
      if (!el) return;
      stickToBottomRef.current = true;
      scrollContainerToBottom(el, behavior);
    },
    [containerRef]
  );

  return { scrollToBottom };
}
